import { useState } from "react";

export interface PaginationState<T> {
  /** Geçerli sayfadaki kayıtlar */
  pageItems: T[];
  /** 1 tabanlı, her zaman [1, totalPages] aralığında */
  page: number;
  totalPages: number;
  totalItems: number;
  pageSize: number;
  setPage: (page: number) => void;
}

/**
 * İstemci tarafı sayfalama.
 * - `resetKey` (ör. arama/filtre değeri) değişince sayfa 1'e döner.
 * - Kayıt sayısı azalırsa (ör. son sayfadaki kayıt silindi) sayfa son geçerli sayfaya sabitlenir;
 *   böylece silme sonrası kullanıcı bulunduğu sayfada kalır.
 */
export function usePagination<T>(
  items: readonly T[],
  pageSize = 20,
  resetKey: unknown = undefined,
): PaginationState<T> {
  const [rawPage, setRawPage] = useState(1);
  const [prevResetKey, setPrevResetKey] = useState<unknown>(resetKey);

  // Render sırasında önceki anahtarla karşılaştır (effect içinde setState yerine).
  let current = rawPage;
  if (!Object.is(prevResetKey, resetKey)) {
    setPrevResetKey(resetKey);
    setRawPage(1);
    current = 1;
  }

  const totalItems = items.length;
  const totalPages = Math.max(1, Math.ceil(totalItems / pageSize));
  const page = Math.min(Math.max(1, current), totalPages);
  const start = (page - 1) * pageSize;

  return {
    pageItems: items.slice(start, start + pageSize),
    page,
    totalPages,
    totalItems,
    pageSize,
    setPage: (next: number) => setRawPage(Math.min(Math.max(1, next), totalPages)),
  };
}

/** Sayfa numaraları; aradaki boşluklar "ellipsis" ile gösterilir. Örn: 1 … 4 5 6 … 12 */
export function pageRange(page: number, totalPages: number): Array<number | "ellipsis"> {
  if (totalPages <= 7) return Array.from({ length: totalPages }, (_, i) => i + 1);

  const pages = new Set<number>([1, totalPages, page - 1, page, page + 1]);
  if (page <= 3) [2, 3, 4].forEach((p) => pages.add(p));
  if (page >= totalPages - 2)
    [totalPages - 3, totalPages - 2, totalPages - 1].forEach((p) => pages.add(p));

  const sorted = [...pages].filter((p) => p >= 1 && p <= totalPages).sort((a, b) => a - b);
  const out: Array<number | "ellipsis"> = [];
  sorted.forEach((p, i) => {
    if (i > 0 && p - sorted[i - 1] > 1) out.push("ellipsis");
    out.push(p);
  });
  return out;
}
